// The ALL stance's page projection: the whole entitlement as one tree.
// Identity → each entitled World → its primary sessions (parentless) → their
// children, nested by parent pointers. Every session belongs to a World
// directly or through its parent chain; ungoverned work gathers under the
// Local group at the end. A pure fold over the same flat thread shells every
// other chrome surface projects — computed per render, persisted nowhere.

export interface EntitlementThreadInput {
  readonly environmentId: string;
  readonly threadId: string;
  readonly worldKey: string | null;
  readonly parentThreadId: string | null;
  readonly title: string | null;
  readonly archivedAt: string | null;
}

export interface EntitlementSessionNode {
  readonly environmentId: string;
  readonly threadId: string;
  readonly title: string | null;
  readonly children: ReadonlyArray<EntitlementSessionNode>;
}

export interface EntitlementWorldGroup {
  /** A membership's World key, or null for the Local (ungoverned) group. */
  readonly worldKey: string | null;
  readonly sessions: ReadonlyArray<EntitlementSessionNode>;
}

function threadKey(thread: { environmentId: string; threadId: string }): string {
  return `${thread.environmentId}:${thread.threadId}`;
}

/**
 * Fold the flat shells into per-World session trees. Rules:
 *
 * - Archived threads (and their seats) are gone; a live child whose parent is
 *   archived, missing, or homed in another World surfaces as a root of its
 *   OWN World — discoverable, never silently invented into a foreign tree
 *   (the A6 orphan law, applied to the overview).
 * - Every entitled World gets a group, sessions or none, in the given order;
 *   the Local group renders only when ungoverned work exists.
 * - Parent pointers are followed within one environment only, with a cycle
 *   guard: a pointer loop breaks into roots rather than recursing forever.
 */
export function projectEntitlementTree(input: {
  readonly worldKeys: ReadonlyArray<string>;
  readonly threads: ReadonlyArray<EntitlementThreadInput>;
}): ReadonlyArray<EntitlementWorldGroup> {
  const live = input.threads.filter((thread) => thread.archivedAt === null);
  const liveByKey = new Map(live.map((thread) => [threadKey(thread), thread]));

  const childrenByParent = new Map<string, EntitlementThreadInput[]>();
  const roots: EntitlementThreadInput[] = [];
  for (const thread of live) {
    const parentKey =
      thread.parentThreadId === null
        ? null
        : threadKey({ environmentId: thread.environmentId, threadId: thread.parentThreadId });
    const parent = parentKey === null ? undefined : liveByKey.get(parentKey);
    // A parent in another World cannot host this child's seat; the child is
    // a root of its own World group.
    if (parent === undefined || parent.worldKey !== thread.worldKey) {
      roots.push(thread);
      continue;
    }
    const siblings = childrenByParent.get(parentKey!) ?? [];
    siblings.push(thread);
    childrenByParent.set(parentKey!, siblings);
  }

  // A parent-pointer loop is reachable from no root and would vanish
  // silently. Break each loop at its first member in input order so every
  // live thread keeps exactly one seat.
  const reachable = new Set<string>();
  const markReachable = (from: EntitlementThreadInput): void => {
    const stack = [from];
    while (stack.length > 0) {
      const next = stack.pop()!;
      const key = threadKey(next);
      if (reachable.has(key)) continue;
      reachable.add(key);
      stack.push(...(childrenByParent.get(key) ?? []));
    }
  };
  for (const root of roots) markReachable(root);
  for (const candidate of live) {
    if (reachable.has(threadKey(candidate))) continue;
    roots.push(candidate);
    markReachable(candidate);
  }

  const buildNode = (
    thread: EntitlementThreadInput,
    visited: ReadonlySet<string>,
  ): EntitlementSessionNode => {
    const key = threadKey(thread);
    const nextVisited = new Set(visited).add(key);
    const children = (childrenByParent.get(key) ?? [])
      .filter((child) => !nextVisited.has(threadKey(child)))
      .map((child) => buildNode(child, nextVisited));
    return {
      environmentId: thread.environmentId,
      threadId: thread.threadId,
      title: thread.title,
      children,
    };
  };

  const rootsByWorld = new Map<string | null, EntitlementSessionNode[]>();
  for (const root of roots) {
    const nodes = rootsByWorld.get(root.worldKey) ?? [];
    nodes.push(buildNode(root, new Set()));
    rootsByWorld.set(root.worldKey, nodes);
  }

  const groups: EntitlementWorldGroup[] = input.worldKeys.map((worldKey) => ({
    worldKey,
    sessions: rootsByWorld.get(worldKey) ?? [],
  }));
  // A thread homed in a World the current grants no longer name still shows —
  // dropped rows would read as "covered everything" when it didn't.
  const entitled = new Set<string | null>([...input.worldKeys, null]);
  for (const [worldKey, sessions] of rootsByWorld) {
    if (!entitled.has(worldKey)) groups.push({ worldKey, sessions });
  }
  const localSessions = rootsByWorld.get(null) ?? [];
  if (localSessions.length > 0) groups.push({ worldKey: null, sessions: localSessions });
  return groups;
}
